import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { EmpresaTenant, MonitoramentoItem, UsuarioWorkflow } from './models';
import workflowService from './workflowService';
import GovernancaContasVencidas from './GovernancaContasVencidas';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { extrairMensagemErro } from './mensagemErro';
import {
  classeNivelAlerta, justificativaOverride, justificativaTrava, lerValorMonetario, moeda, OverrideTemporario,
  podeAlterarTrava, rotuloNivelAlerta, rotuloValidade, TravaConfig, TravaResposta, validarTrava, VALIDADES_OVERRIDE,
} from './travaGovernanca';

type Aviso = { tipo: 'ok' | 'erro'; texto: string } | null;

const configDe = (t: TravaResposta): TravaConfig => ({
  limiteDividaTecnica: t.limiteDividaTecnica, percentualAlerta: t.percentualAlerta,
  hardStopAtivo: t.hardStopAtivo, failSafeAtivo: t.failSafeAtivo, evidenciaObrigatoria: t.evidenciaObrigatoria,
});

const dataHora = (iso: string) => new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString('pt-BR');

const POLITICAS: { chave: 'hardStopAtivo' | 'failSafeAtivo' | 'evidenciaObrigatoria'; nome: string; descricao: string }[] = [
  { chave: 'hardStopAtivo', nome: 'Hard Stop sistêmico', descricao: 'Bloqueia aprovação com SLA vencido, divergência sem justificativa e plano de melhoria aberto — e novas conciliações ao atingir o limite de dívida técnica.' },
  { chave: 'failSafeAtivo', nome: 'Fail-Safe', descricao: 'Exige plano de ação corretiva em todo lançamento manual.' },
  { chave: 'evidenciaObrigatoria', nome: 'Evidência obrigatória', descricao: 'Não permite avançar da etapa Evidências sem ao menos uma evidência anexada.' },
];

/**
 * Governança de TI (Nível 7) — tudo com dados reais: as políticas de trava são gravadas e aplicadas pelo backend
 * (aprovação, criação de conciliação e lançamento manual), as exceções temporárias expiram sozinhas e todo ajuste
 * gera registro SOX com justificativa.
 */
const GovernancaPage: React.FC = () => {
  const history = useHistory();
  const [perfil, setPerfil] = useState<UsuarioWorkflow | null>(null);
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [trava, setTrava] = useState<TravaResposta | null>(null);
  const [vencidas, setVencidas] = useState<MonitoramentoItem[]>([]);
  const [overrides, setOverrides] = useState<OverrideTemporario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [aviso, setAviso] = useState<Aviso>(null);

  // Formulário das políticas
  const [limiteTexto, setLimiteTexto] = useState('');
  const [percentualTexto, setPercentualTexto] = useState('80');
  const [flags, setFlags] = useState({ hardStopAtivo: true, failSafeAtivo: true, evidenciaObrigatoria: true });
  const [salvandoTrava, setSalvandoTrava] = useState(false);

  // Exceção temporária
  const [empresaOverrideId, setEmpresaOverrideId] = useState('');
  const [validade, setValidade] = useState(4);
  const [criandoOverride, setCriandoOverride] = useState(false);
  const [revogando, setRevogando] = useState<number | null>(null);
  const [justRevogar, setJustRevogar] = useState('');

  const podeAlterar = podeAlterarTrava(perfil);

  const formAtual: TravaConfig = {
    limiteDividaTecnica: lerValorMonetario(limiteTexto) || 0,
    percentualAlerta: Number(percentualTexto) || 0,
    ...flags,
  };
  const justificativaPolitica = useJustificativaSugerida(trava ? justificativaTrava(configDe(trava), formAtual) : '');

  const empresaOverride = empresas.find(e => String(e.id) === empresaOverrideId);
  const motivoOverride = useJustificativaSugerida(empresaOverride ? justificativaOverride(empresaOverride.nome, validade) : '');

  const aplicarTrava = (t: TravaResposta) => {
    setTrava(t);
    setLimiteTexto(String(t.limiteDividaTecnica).replace('.', ','));
    setPercentualTexto(String(t.percentualAlerta));
    setFlags({ hardStopAtivo: t.hardStopAtivo, failSafeAtivo: t.failSafeAtivo, evidenciaObrigatoria: t.evidenciaObrigatoria });
    justificativaPolitica.restaurar();
  };

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [t, ov, mon] = await Promise.all([
        workflowService.obterTrava(),
        workflowService.listarOverrides().catch(() => [] as OverrideTemporario[]),
        workflowService.listarMonitoramento().catch(() => null),
      ]);
      if (t && typeof t.limiteDividaTecnica === 'number') aplicarTrava(t);
      setOverrides(Array.isArray(ov) ? ov : []);
      setVencidas((mon?.itens ?? []).filter(i => i.slaStatus === 'VERMELHO'));
    } catch (err) {
      setAviso({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível carregar as políticas de trava.') });
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    workflowService.getMeuPerfil().then(setPerfil).catch(() => setPerfil(null));
    workflowService.listarTenants().then(l => setEmpresas(l.filter(e => e.status === 'ATIVA'))).catch(() => setEmpresas([]));
    carregar();
  }, [carregar]);

  const salvarPoliticas = async () => {
    const erro = validarTrava(limiteTexto, formAtual.percentualAlerta, justificativaPolitica.texto);
    if (erro) { setAviso({ tipo: 'erro', texto: erro }); return; }
    setAviso(null);
    setSalvandoTrava(true);
    try {
      const salva = await workflowService.salvarTrava({ ...formAtual, justificativaSox: justificativaPolitica.texto.trim() });
      if (salva && typeof salva.limiteDividaTecnica === 'number') aplicarTrava(salva);
      setAviso({ tipo: 'ok', texto: 'Políticas salvas. Registro SOX gerado e já valendo para aprovações, conciliações e lançamentos.' });
    } catch (err) {
      setAviso({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível salvar as políticas.') });
    } finally {
      setSalvandoTrava(false);
    }
  };

  const criarOverride = async () => {
    if (!empresaOverrideId) { setAviso({ tipo: 'erro', texto: 'Selecione a empresa da exceção.' }); return; }
    if (!motivoOverride.texto.trim()) { setAviso({ tipo: 'erro', texto: 'Informe o motivo da exceção temporária.' }); return; }
    setAviso(null);
    setCriandoOverride(true);
    try {
      await workflowService.criarOverride({ empresaId: Number(empresaOverrideId), motivo: motivoOverride.texto.trim(), validadeHoras: validade });
      setEmpresaOverrideId('');
      motivoOverride.restaurar();
      setAviso({ tipo: 'ok', texto: `Exceção temporária criada por ${rotuloValidade(validade)}. Ela expira sozinha e gerou registro SOX.` });
      setOverrides(await workflowService.listarOverrides());
    } catch (err) {
      setAviso({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível criar a exceção temporária.') });
    } finally {
      setCriandoOverride(false);
    }
  };

  const confirmarRevogacao = async (id: number) => {
    if (!justRevogar.trim()) { setAviso({ tipo: 'erro', texto: 'Informe a justificativa SOX para revogar a exceção.' }); return; }
    setAviso(null);
    try {
      await workflowService.revogarOverride(id, justRevogar.trim());
      setRevogando(null);
      setJustRevogar('');
      setAviso({ tipo: 'ok', texto: 'Exceção revogada. Registro SOX gerado.' });
      setOverrides(await workflowService.listarOverrides());
    } catch (err) {
      setAviso({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível revogar a exceção.') });
    }
  };

  const ativas = overrides.filter(o => o.status === 'ATIVO');
  const pct = Math.min(100, trava?.percentualUtilizado ?? 0);

  return (
    <div className='wf-page'>
      <div className='wf-header'>
        <div><h1>Governança de TI — Nível 7</h1><p>Travas sistêmicas, políticas, exceções e responsabilidade de governança.</p></div>
        <div>
          <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>
          <span className='wf-level7' style={{ marginLeft: 10 }}>Acesso exclusivo Nível 7</span>
        </div>
      </div>

      {aviso && (
        <div className={aviso.tipo === 'ok' ? 'wf-toast wf-toast--success' : 'wf-review'} role={aviso.tipo === 'erro' ? 'alert' : undefined}>
          {aviso.tipo === 'erro' && <strong>Atenção</strong>}<p style={{ margin: 0 }}>{aviso.texto}</p>
        </div>
      )}
      {!podeAlterar && perfil && (
        <p className='wf-count'>Somente o Administrador de TI ou o Nível 7 pode alterar as políticas e as exceções. Você pode consultar.</p>
      )}

      {carregando && <p className='wf-count'>Carregando...</p>}

      {trava && (
        <>
          <div className='wf-metrics' data-testid='indicadores-governanca'>
            <div className={`wf-metric wf-metric--${classeNivelAlerta(trava.nivelAlerta)}`}>
              <span>Dívida técnica atual</span><strong>{moeda(trava.dividaTecnicaAtual)}</strong>
              <small>{trava.percentualUtilizado.toLocaleString('pt-BR')}% do limite de {moeda(trava.limiteDividaTecnica)}</small>
            </div>
            <div className='wf-metric wf-metric--danger'>
              <span>Contas vencidas</span><strong>{vencidas.length}</strong><small>SLA expirado — ação da governança</small>
            </div>
            <div className='wf-metric wf-metric--warning'>
              <span>Exceções ativas</span><strong>{ativas.length}</strong><small>liberações temporárias vigentes</small>
            </div>
            <div className='wf-metric wf-metric--info'>
              <span>Trava</span>
              <strong>{trava.hardStopAtivo ? 'Ativa' : 'Desativada'}</strong>
              <small>{trava.dataUltimaAlteracao ? `última alteração ${dataHora(trava.dataUltimaAlteracao)}` : 'política padrão (nunca alterada)'}</small>
            </div>
          </div>

          <div className='wf-cobertura' style={{ marginBottom: 20 }}>
            <div className='wf-cobertura__topo'>
              <span>{rotuloNivelAlerta(trava.nivelAlerta, trava.percentualAlerta)}</span><strong>{trava.percentualUtilizado.toLocaleString('pt-BR')}%</strong>
            </div>
            <div className='wf-cobertura__trilho' role='progressbar' aria-label='Dívida técnica' aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
              <div className={`wf-cobertura__barra wf-cobertura__barra--${classeNivelAlerta(trava.nivelAlerta)}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </>
      )}

      <div className='wf-grid-2'>
        <section className='wf-card'>
          <div className='wf-card__header'><div><h2>Configuração de trava</h2><p>Limite de dívida técnica e políticas de bloqueio.</p></div></div>
          <div className='wf-form-grid wf-form-grid--one'>
            <label>Limite de dívida técnica (R$)
              <input value={limiteTexto} onChange={e => { const v = e.target.value; setLimiteTexto(v); }} disabled={!podeAlterar || !trava} inputMode='decimal' aria-label='Limite de dívida técnica' />
            </label>
            <label>Alertar a partir de (% do limite)
              <input value={percentualTexto} onChange={e => { const v = e.target.value; setPercentualTexto(v); }} disabled={!podeAlterar || !trava} inputMode='numeric' aria-label='Percentual de alerta' />
            </label>
          </div>
          <div className='wf-switches'>
            {POLITICAS.map(p => (
              <label key={p.chave}>
                <div><strong>{p.nome}</strong><span>{p.descricao}</span></div>
                <input type='checkbox' checked={flags[p.chave]} disabled={!podeAlterar || !trava} aria-label={p.nome}
                  onChange={e => { const v = e.target.checked; setFlags(f => ({ ...f, [p.chave]: v })); }} />
              </label>
            ))}
          </div>
          <label style={{ display: 'block', marginTop: 12 }}>
            Justificativa SOX <span style={{ color: '#f64e60', fontSize: 10 }}>(obrigatória)</span>
            <CampoJustificativaSox justificativa={justificativaPolitica} rows={3} placeholder='Motivo do ajuste das políticas...' />
          </label>
          <button className='wf-btn wf-btn--primary' style={{ marginTop: 12 }} onClick={salvarPoliticas} disabled={!podeAlterar || !trava || salvandoTrava}>
            {salvandoTrava ? 'Salvando...' : 'Salvar políticas'}
          </button>
        </section>

        <section className='wf-card'>
          <div className='wf-card__header'><div><h2>Override temporário</h2><p>Liberação excepcional com expiração automática.</p></div></div>
          <div className='wf-form-grid wf-form-grid--one'>
            <label>Empresa
              <select value={empresaOverrideId} onChange={e => { const v = e.target.value; setEmpresaOverrideId(v); }} disabled={!podeAlterar} aria-label='Empresa da exceção'>
                <option value=''>— Selecione a empresa —</option>
                {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </label>
            <label>Validade
              <select value={validade} onChange={e => { const v = Number(e.target.value); setValidade(v); }} disabled={!podeAlterar} aria-label='Validade da exceção'>
                {VALIDADES_OVERRIDE.map(h => <option key={h} value={h}>{rotuloValidade(h)}</option>)}
              </select>
            </label>
            <label>Motivo <span style={{ color: '#f64e60', fontSize: 10 }}>(obrigatório)</span>
              <CampoJustificativaSox justificativa={motivoOverride} rows={3} placeholder='Descreva o motivo da liberação excepcional...' />
            </label>
          </div>
          {perfil && (
            <div className='wf-owner'><span>Responsável pela Governança</span>
              <strong>{perfil.nome}{perfil.nivel ? ` — N${perfil.nivel}` : ''}{perfil.cargo ? ` / ${perfil.cargo}` : ''}</strong>
            </div>
          )}
          <button className='wf-btn wf-btn--warning' onClick={criarOverride} disabled={!podeAlterar || criandoOverride}>
            {criandoOverride ? 'Criando...' : 'Criar exceção temporária'}
          </button>
        </section>
      </div>

      <section className='wf-card' style={{ marginTop: 20 }}>
        <div className='wf-card__header'><div><h2>Exceções temporárias</h2><p>Vigentes e recentes das empresas do seu cliente.</p></div></div>
        {overrides.length === 0 ? <p className='wf-count'>Nenhuma exceção temporária registrada.</p> : (
          <div className='wf-table-wrap'><table className='wf-table'>
            <thead><tr><th>Empresa</th><th>Motivo</th><th>Criada por</th><th>Expira em</th><th>Status</th><th /></tr></thead>
            <tbody>{overrides.map(o => (
              <React.Fragment key={o.overrideId}>
                <tr>
                  <td><strong>{o.nomeEmpresa}</strong><span>{rotuloValidade(o.validadeHoras)}</span></td>
                  <td style={{ maxWidth: 280, fontSize: 12 }}>{o.motivo}</td>
                  <td>{o.criadoPorNome}</td>
                  <td>{dataHora(o.expiraEm)}</td>
                  <td><span className={`wf-badge ${o.status === 'ATIVO' ? 'wf-badge--aprovada' : 'wf-badge--bloqueada'}`}>{o.status}</span></td>
                  <td>{o.status === 'ATIVO' && podeAlterar && (
                    <button className='wf-link' onClick={() => { setRevogando(revogando === o.overrideId ? null : o.overrideId); setJustRevogar(''); }}>Revogar</button>
                  )}</td>
                </tr>
                {revogando === o.overrideId && (
                  <tr><td colSpan={6}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Justificativa SOX da revogação (obrigatória)
                      <textarea rows={2} style={{ width: '100%' }} value={justRevogar} onChange={e => { const v = e.target.value; setJustRevogar(v); }} placeholder='Motivo da revogação...' />
                    </label>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                      <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={() => confirmarRevogacao(o.overrideId)}>Confirmar revogação</button>
                      <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => setRevogando(null)}>Cancelar</button>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}</tbody>
          </table></div>
        )}
      </section>

      <GovernancaContasVencidas />
    </div>
  );
};

export default GovernancaPage;
